import { Copy, Trash2 } from "lucide-react";
import { useState } from "react";
import { GradientConfig, SvgOverlay } from "../models/Post";
import { GradientPicker } from "./GradientPicker";
import { HexColorInput } from "./HexColorInput";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import { Switch } from "./ui/switch";
import {
  BrandColorPickerSwatch,
  type BrandKitColorPickerContext,
  type BrandColorShortcut,
} from "./BrandColorShortcuts";

interface SvgOverlayFormatControlsProps {
  svgOverlay: SvgOverlay;
  onChange: (updatedOverlay: SvgOverlay) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  showDeleteButton?: boolean;
  showDuplicateButton?: boolean;
  onChangeSvgUpload?: (id: string) => void;
  brandColors?: BrandColorShortcut[];
  brandKitContext?: BrandKitColorPickerContext;
}

export function SvgOverlayFormatControls({
  svgOverlay,
  onChange,
  onDelete,
  onDuplicate,
  showDeleteButton = true,
  showDuplicateButton = true,
  onChangeSvgUpload,
  brandColors,
  brandKitContext,
}: SvgOverlayFormatControlsProps) {
  // Track whether fill/stroke overrides are enabled
  const [fillEnabled, setFillEnabled] = useState(!!svgOverlay.fillColor);
  const [strokeEnabled, setStrokeEnabled] = useState(!!svgOverlay.strokeColor);

  const handleOpacityChange = (value: number[]) => {
    onChange({ ...svgOverlay, opacity: value[0] });
  };

  const handleRotationChange = (value: number[]) => {
    onChange({ ...svgOverlay, rotation: value[0] });
  };

  const handleFillColorChange = (color: string | GradientConfig) => {
    onChange({ ...svgOverlay, fillColor: color });
  };

  const handleFillToggle = (enabled: boolean) => {
    setFillEnabled(enabled);
    if (!enabled) {
      // Remove fill override
      const { fillColor, ...rest } = svgOverlay;
      onChange(rest as SvgOverlay);
    } else {
      // Set default fill color
      onChange({ ...svgOverlay, fillColor: "#000000" });
    }
  };

  const handleStrokeColorChange = (color: string) => {
    onChange({ ...svgOverlay, strokeColor: color });
  };

  const handleStrokeWidthChange = (value: number[]) => {
    onChange({ ...svgOverlay, strokeWidth: value[0] });
  };

  const handleStrokeToggle = (enabled: boolean) => {
    setStrokeEnabled(enabled);
    if (!enabled) {
      // Remove stroke overrides
      const { strokeColor, strokeWidth, ...rest } = svgOverlay;
      onChange(rest as SvgOverlay);
    } else {
      // Set default stroke
      onChange({ ...svgOverlay, strokeColor: "#000000", strokeWidth: 2 });
    }
  };

  return (
    <div className="space-y-6">
      {/* Change SVG Button */}
      {onChangeSvgUpload && (
        <div className="space-y-2 pb-4 border-b">
          <Label>SVG Source</Label>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => onChangeSvgUpload(svgOverlay.id)}
          >
            Change SVG
          </Button>
        </div>
      )}

      {/* Color (supports solid colors and gradients) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label htmlFor="fill-toggle">Custom Color</Label>
          <Switch
            id="fill-toggle"
            checked={fillEnabled}
            onCheckedChange={handleFillToggle}
          />
        </div>
        {fillEnabled && (
          <GradientPicker
            value={svgOverlay.fillColor}
            onChange={handleFillColorChange}
            label=""
            brandColors={brandColors}
            brandKitContext={brandKitContext}
          />
        )}
      </div>

      {/* Custom Stroke (when you want stroke different from fill) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label htmlFor="stroke-toggle">Custom Stroke</Label>
          <Switch
            id="stroke-toggle"
            checked={strokeEnabled}
            onCheckedChange={handleStrokeToggle}
          />
        </div>
        {strokeEnabled && (
          <>
            <div className="flex items-center gap-2">
              <BrandColorPickerSwatch
                id="stroke-color"
                value={svgOverlay.strokeColor || "#000000"}
                onChange={handleStrokeColorChange}
                className="h-10 w-10"
                brandColors={brandColors}
                brandKitContext={brandKitContext}
                ariaLabel="Choose SVG stroke color"
              />
              <HexColorInput
                value={svgOverlay.strokeColor || "#000000"}
                onChange={handleStrokeColorChange}
                className="flex-1 text-sm"
                aria-label="Stroke hex color"
              />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>Stroke Width</Label>
                <span className="text-sm text-muted-foreground">
                  {svgOverlay.strokeWidth || 2}px
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">0</span>
                <Slider
                  value={[svgOverlay.strokeWidth || 2]}
                  onValueChange={handleStrokeWidthChange}
                  max={20}
                  min={0}
                  step={0.5}
                />
                <span className="text-xs text-muted-foreground">20</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Opacity */}
      <div className="space-y-2">
        <div className="flex justify-between">
          <Label>Opacity</Label>
          <span className="text-sm text-muted-foreground">
            {svgOverlay.opacity}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">0</span>
          <Slider
            value={[svgOverlay.opacity]}
            onValueChange={handleOpacityChange}
            max={100}
            step={1}
          />
          <span className="text-xs text-muted-foreground">100</span>
        </div>
      </div>

      {/* Rotation */}
      <div className="space-y-2">
        <div className="flex justify-between">
          <Label>Rotation</Label>
          <span className="text-sm text-muted-foreground">
            {svgOverlay.rotation || 0}°
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">-180</span>
          <Slider
            value={[svgOverlay.rotation || 0]}
            onValueChange={handleRotationChange}
            max={180}
            min={-180}
            step={1}
          />
          <span className="text-xs text-muted-foreground">180</span>
        </div>
      </div>

      {/* Duplicate & Delete */}
      {(showDuplicateButton || showDeleteButton) && (
        <div className="space-y-2 pt-4 border-t">
          {showDuplicateButton && (
            <Button
              variant="outline"
              className="w-full flex items-center justify-center gap-2"
              onClick={() => onDuplicate(svgOverlay.id)}
            >
              <Copy size={16} />
              Duplicate
            </Button>
          )}
          {showDeleteButton && (
            <Button
              variant="outline"
              className="w-full flex items-center justify-center gap-2 border-red-500 text-red-500 hover:bg-red-50 hover:text-red-700 focus-visible:ring-red-400"
              onClick={() => onDelete(svgOverlay.id)}
            >
              <Trash2 size={16} />
              Delete
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
